import { UserCheck } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import { getAvatarUrl } from '../../utils/avatarUtils'

const FormContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.textMuted};
`

const LogoText = styled.h1`
  font-size: ${theme.typography.xl};
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  width: 100%;
  text-align: center;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: contain;
  margin: 0 auto;
`

const FormTitle = styled.h1`
  font-size: ${theme.typography.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
`

const CoachInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary}20;
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: ${theme.spacing.xl};

  .coach-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    font-weight: 600;
    font-size: ${theme.typography.sm};
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .coach-details {
    flex: 1;

    .coach-label {
      font-size: ${theme.typography.xs};
      color: ${({ theme }) => theme.colors.textMuted};
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 2px;
    }

    .coach-name {
      font-size: ${theme.typography.md};
      color: ${({ theme }) => theme.colors.text};
      font-weight: 600;
      margin: 0;
    }
  }

  .verified-icon {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const FormLabel = styled.label`
  font-size: ${theme.typography.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const FormInput = styled.input`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    opacity: 0.6;
  }
`

const SubmitButton = styled.button<{ $loading?: boolean }>`
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.textInverted};
  font-size: ${theme.typography.md};
  font-weight: 600;
  cursor: ${(props) => (props.$loading ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.default};
  margin-top: ${theme.spacing.sm};

  &:hover {
    background-color: ${({ theme, $loading }) => ($loading ? theme.colors.primary : theme.colors.primaryDark)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  color: #ff6b6b;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

const SuccessMessage = styled.div`
  background-color: rgba(151, 251, 87, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
  text-align: center;
`

const InfoMessage = styled.div`
  background-color: ${theme.colors.secondaryDark};
  border: 1px solid ${theme.colors.secondary};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${theme.colors.textInverted};
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
  text-align: center;
`

const PrivacyNotice = styled.div`
  text-align: center;
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${theme.spacing.md};
  line-height: 1.4;

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.5;
`

interface CoachData {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  avatar_url: string | null
}

interface AthleteSignupFormProps {
  coachId: string
  onSignupComplete?: () => void
}

export const AthleteSignupForm: React.FC<AthleteSignupFormProps> = ({ coachId, onSignupComplete }) => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [coachData, setCoachData] = useState<CoachData | null>(null)
  const [loadingCoach, setLoadingCoach] = useState(true)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [loadingUser, setLoadingUser] = useState(true)
  const [alreadyConnected, setAlreadyConnected] = useState(false)

  // Load current user information if signed in
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        setLoadingUser(true)
        const {
          data: { user },
        } = await supabase.auth.getUser()

        if (user) {
          setCurrentUser(user)
          setEmail(user.email || '')

          // Load user profile to get first and last name
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single()

          if (profile) {
            setFirstName(profile.first_name || '')
            setLastName(profile.last_name || '')
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err)
      } finally {
        setLoadingUser(false)
      }
    }

    loadCurrentUser()
  }, [])

  // Check if user is already connected to this coach
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (!currentUser || !coachId) return

      try {
        const { data, error } = await supabase
          .from('athlete_to_coach')
          .select('id')
          .eq('athlete_id', currentUser.id)
          .eq('coach_id', coachId)
          .maybeSingle()

        if (error) {
          console.error('Error checking connection:', error)
          return
        }

        if (data) {
          setAlreadyConnected(true)
        }
      } catch (err) {
        console.error('Error checking existing connection:', err)
      }
    }

    checkExistingConnection()
  }, [currentUser, coachId])

  // Load coach information
  useEffect(() => {
    const loadCoachData = async () => {
      if (!coachId) {
        setError('Invalid coach invitation link')
        setLoadingCoach(false)
        return
      }

      try {
        setLoadingCoach(true)
        const { data, error } = await supabase.rpc('get_coach_info_for_signup' as any, {
          coach_id: coachId,
        })

        if (error) {
          console.error('Error loading coach info:', error)
          setError('Coach not found')
          return
        }

        if (!data || data.length === 0) {
          setError('Coach not found or not available for signup')
          return
        }

        setCoachData(data[0])
      } catch (err) {
        console.error('Error loading coach data:', err)
        setError('Failed to load coach information')
      } finally {
        setLoadingCoach(false)
      }
    }

    loadCoachData()
  }, [coachId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    if (!coachData) {
      setError('Coach information not loaded')
      setLoading(false)
      return
    }

    try {
      let userId: string

      if (currentUser) {
        // User is already signed in - just connect them to the coach
        userId = currentUser.id
      } else {
        // Validate password length
        if (password.length < 8) {
          setError('Password must be at least 8 characters long')
          setLoading(false)
          return
        }

        // Create a new user account
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName,
            },
          },
        })

        if (authError) {
          setError(authError.message)
          return
        }

        if (!authData.user) {
          setError('Failed to create user account')
          return
        }

        userId = authData.user.id
      }

      if (!currentUser) {
        // Remove the default coach role that was automatically added by the database trigger
        const { error: deleteRoleError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', userId)
          .eq('role', 'coach')
        if (deleteRoleError) {
          console.error('Error removing coach role:', deleteRoleError)
        }

        // Add athlete role to the user
        const { error: roleError } = await supabase.rpc('add_user_role', {
          target_role: 'athlete',
        })
        if (roleError) {
          console.error('Error adding athlete role:', roleError)
          // Don't fail the signup if role addition fails
        }
      }

      // Create the athlete-to-coach connection
      const { error: connectionError } = await supabase.from('athlete_to_coach').insert({
        athlete_id: userId,
        coach_id: coachId,
      })

      if (connectionError) {
        console.error('Error creating coach connection:', connectionError)
        // Check if connection already exists
        if (connectionError.code === '23505') {
          setError('You are already connected to this coach')
          return
        }
        setError('Failed to create coach connection')
        return
      }

      const coachName = coachData.first_name || coachData.email
      if (currentUser) {
        setSuccess(`Success! You've been connected to ${coachName}.`)
      } else {
        setSuccess(`Welcome! Your account has been created and you've been connected to ${coachName}.`)
      }

      if (onSignupComplete) {
        onSignupComplete()
      }
    } catch (err) {
      console.error('Signup error:', err)
      setError('An unexpected error occurred during signup')
    } finally {
      setLoading(false)
    }
  }

  const handleContinue = () => {
    // Navigate to the root path which will redirect to the appropriate dashboard based on role
    navigate('/')
  }

  if (loadingCoach || loadingUser) {
    return (
      <FormContainer>
        <LogoContainer>
          <Logo src="/logo_512.png" alt="supacoach Logo" />
          <LogoText>supacoach</LogoText>
        </LogoContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
      </FormContainer>
    )
  }

  if (!coachData) {
    return (
      <FormContainer>
        <LogoContainer>
          <Logo src="/logo_512.png" alt="supacoach Logo" />
          <LogoText>supacoach</LogoText>
        </LogoContainer>
        <ErrorMessage>{error || 'Coach not found. Please check your invitation link.'}</ErrorMessage>
      </FormContainer>
    )
  }

  const coachDisplayName = `${coachData.first_name || ''} ${coachData.last_name || ''}`.trim() || coachData.email
  const coachInitials = coachData.first_name?.[0] + (coachData.last_name?.[0] || '') || coachData.email[0].toUpperCase()
  const coachAvatarUrl = getAvatarUrl(coachData.avatar_url)

  if (success !== null) {
    return (
      <FormContainer>
        <LogoContainer>
          <Logo src="/logo_512.png" alt="supacoach Logo" />
          <LogoText>supacoach</LogoText>
        </LogoContainer>

        <FormTitle>Join {coachDisplayName}'s Coaching Program</FormTitle>

        <CoachInfo>
          <div className="coach-avatar">
            {coachAvatarUrl ? <img src={coachAvatarUrl} alt={coachDisplayName} /> : coachInitials}
          </div>
          <div className="coach-details">
            <div className="coach-label">Coach</div>
            <div className="coach-name">{coachDisplayName}</div>
          </div>
          <UserCheck size={20} className="verified-icon" />
        </CoachInfo>

        <SuccessMessage>{success}</SuccessMessage>

        <Form onSubmit={handleContinue}>
          <SubmitButton type="submit" disabled={loading}>
            Continue to Dashboard
          </SubmitButton>
        </Form>
      </FormContainer>
    )
  }

  return (
    <FormContainer>
      <LogoContainer>
        <Logo src="/logo_512.png" alt="supacoach Logo" />
        <LogoText>supacoach</LogoText>
      </LogoContainer>

      <FormTitle>Join {coachDisplayName}'s Coaching Program</FormTitle>

      <CoachInfo>
        <div className="coach-avatar">
          {coachAvatarUrl ? <img src={coachAvatarUrl} alt={coachDisplayName} /> : coachInitials}
        </div>
        <div className="coach-details">
          <div className="coach-label">Coach</div>
          <div className="coach-name">{coachDisplayName}</div>
        </div>
        <UserCheck size={20} className="verified-icon" />
      </CoachInfo>

      <Description>You've been invited to join {coachDisplayName}'s coaching program.</Description>
      <Description>
        After joining {coachDisplayName} will be able to see your workouts until you leave the program. <br />
      </Description>

      <Form onSubmit={handleSubmit}>
        {currentUser === null && (
          <>
            <FormField>
              <FormLabel>First Name</FormLabel>
              <FormInput
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your given name"
                required
              />
            </FormField>

            <FormField>
              <FormLabel>Last Name</FormLabel>
              <FormInput
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Your family name"
                required
              />
            </FormField>

            <FormField>
              <FormLabel>Email Address</FormLabel>
              <FormInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />
            </FormField>

            <FormField>
              <FormLabel>Password</FormLabel>
              <FormInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a strong password"
                minLength={8}
                required
              />
              <Description>Your password must contain lowercase, uppercase letters, digits and symbols.</Description>
            </FormField>
          </>
        )}

        {!alreadyConnected && (
          <SubmitButton type="submit" $loading={loading} disabled={loading}>
            {loading ? (currentUser ? 'Connecting...' : 'Creating Account...') : 'Join Training Program'}
          </SubmitButton>
        )}

        {alreadyConnected && <InfoMessage>You are already connected to coach {coachDisplayName}.</InfoMessage>}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <PrivacyNotice>
          By creating an account, you agree to our{' '}
          <a href="https://www.supacoach.ai/privacy/" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
        </PrivacyNotice>
      </Form>
    </FormContainer>
  )
}
